import { useDataGrid, List, ShowButton, EditButton } from '@refinedev/mui'
import { DataGrid, GridColDef } from '@mui/x-data-grid'
import { Typography, CircularProgress, Link, Button } from '@mui/material'

import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord'
import { useList, useGetIdentity, useOne } from '@refinedev/core'
import { User } from '@/utils/auth/auth0'

export default function UserSpecificOpportunitiesList() {
  const user = useGetIdentity().data as User

  const { data: userData, isLoading } = useOne({
    resource: 'user-by-email',
    id: user?.email,
    queryOptions: {
      enabled: !!user,
    },
  })

  const { dataGridProps } = useDataGrid({
    resource: 'assigned-opportunities',
    meta: {
      user_uuid: userData,
    },
    queryOptions: {
      enabled: !!userData,
    },
    errorNotification: (error) => {
      return {
        message: 'Error getting assigned opportunities',
        description:
          error?.message ||
          'An error occurred while fetching assigned opportunities',
        type: 'error',
      }
    },
  })

  const columns: GridColDef[] = [
    {
      field: 'fund_name',
      headerName: 'Name',
      flex: 1,
      minWidth: 300,
    },
    {
      field: 'traffic_light_status',
      headerName: 'Status',
      flex: 0.2,
      minWidth: 70,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      renderCell: function render({ row }) {
        const colorMap: Record<string, string> = {
          green: '#4caf50',
          yellow: '#ff9800',
          red: '#f44336',
        }
        const color = colorMap[row.traffic_light_status] ?? '#9e9e9e'
        return <FiberManualRecordIcon sx={{ color, fontSize: 28 }} />
      },
    },
    {
      field: 'flag_for_updates',
      headerName: 'Flag',
      sortable: true,
      renderCell: function render({ row }) {
        if (!row.flag_for_updates) return null
        return <Typography>{row.flag_for_updates || ''}</Typography>
      },
      minWidth: 150,
      flex: 1,
    },
    {
      field: 'next_check_in_date',
      headerName: 'Next Check-in',
      sortable: true,
      renderCell: function render({ row }) {
        return (
          <Typography>
            {row.next_check_in_date
              ? new Date(row.next_check_in_date).toLocaleDateString('en-NZ', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric',
                })
              : ''}
          </Typography>
        )
      },
      maxWidth: 130,
      align: 'center',
      flex: 1,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      sortable: false,
      renderCell: function render({ row }) {
        return (
          <>
            <ShowButton
              hideText
              resource="opportunities"
              recordItemId={row.uuid}
            />
            <EditButton
              hideText
              resource="opportunities"
              recordItemId={row.uuid}
            />
          </>
        )
      },

      maxWidth: 100,
    },
  ]

  if (isLoading) {
    return <CircularProgress size={20} />
  }

  return (
    <>
      <Typography variant="h5" sx={{ mt: 1, mb: 3 }}>
        {user?.email && userData ? (
          <Button variant="contained" sx={{ mb: 2, color: 'white' }}>
            <Link href={`/opportunities`} style={{ color: 'white' }}>
              Back to Full List
            </Link>
          </Button>
        ) : (
          'Loading...'
        )}
      </Typography>
      <List>
        <DataGrid
          {...dataGridProps}
          columns={columns}
          autoHeight
          getRowId={(row) => row.uuid}
        />
      </List>
    </>
  )
}
